/**
 * YABA analytics — GA4 + Meta Pixel unified wrapper.
 *
 * Design:
 *  - Consent-gated: nothing fires until the visitor has accepted analytics/marketing.
 *  - No-op safe: if REACT_APP_GA_MEASUREMENT_ID or REACT_APP_META_PIXEL_ID are missing,
 *    the corresponding platform is silently skipped.
 *  - Single API surface: trackEvent(name, params) dispatches to both platforms with the
 *    right event names and payload shapes.
 */

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const META_ID = process.env.REACT_APP_META_PIXEL_ID || "";

let gaLoaded = false;
let metaLoaded = false;
let consent = { analytics: false, marketing: false };

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export function getConsent() {
  return { ...consent };
}

export function setAnalyticsConsent(next) {
  consent = { ...consent, ...next };

  if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
    });
  }
  if (window.fbq) {
    window.fbq("consent", consent.marketing ? "grant" : "revoke");
  }
}

export async function initAnalytics(userConsent = consent) {
  consent = { ...consent, ...userConsent };

  // GA4
  if (consent.analytics && GA_ID && !gaLoaded) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
    });
    window.gtag("js", new Date());
    // send_page_view:false → we send SPA page_views ourselves via RouteAnalytics.
    window.gtag("config", GA_ID, { send_page_view: false });

    try {
      await loadScript(
        `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`,
        "ga4-script"
      );
      gaLoaded = true;
    } catch (e) {
      // Ad blocker or network — silently skip.
    }
  }

  // Meta Pixel
  if (consent.marketing && META_ID && !metaLoaded) {
    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js"
    );
    /* eslint-enable */

    window.fbq("consent", "grant");
    window.fbq("init", META_ID);
    metaLoaded = true;
  }
}

// GA4 event → Meta standard/custom event
const metaName = {
  page_view: "PageView",
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
  lead: "Lead",
  sign_up: "Lead",
  remove_from_cart: "RemoveFromCart", // custom
  view_cart: "ViewCart", // custom
};

export function trackEvent(name, params = {}) {
  // GA4
  if (consent.analytics && window.gtag) {
    window.gtag("event", name, params);
  }

  // Meta Pixel
  if (consent.marketing && window.fbq && metaName[name]) {
    const {
      items = [],
      currency,
      value,
      transaction_id,
      coupon,
      method,
      ...rest
    } = params;

    const contents = items.map((item) => ({
      id: String(item.item_id),
      quantity: Number(item.quantity || 1),
      item_price: Number(item.price || 0),
    }));

    const metaParams = {
      ...rest,
      currency,
      value,
      content_type: "product",
      content_ids: contents.map((x) => x.id),
      contents,
      num_items: contents.reduce((a, c) => a + c.quantity, 0),
    };

    if (name === "purchase" && transaction_id) {
      // eventID lets us dedupe with Conversions API later.
      window.fbq("track", "Purchase", metaParams, { eventID: transaction_id });
    } else if (name === "sign_up" || name === "lead") {
      window.fbq("track", "Lead", { method, currency, value });
    } else {
      window.fbq("track", metaName[name], metaParams);
    }
  }
}
